import { useState, useEffect } from "react";
import axios from "axios";
import {
  Building2, Package, AlertTriangle, TrendingUp, Euro, CheckCircle2,
  Clock, Sparkles, ArrowRight, Calendar, Bell, X, Truck, ChevronRight,
  ClipboardList, Users
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SkeletonCard() {
  return (
    <div className="skeleton-card">
      <div className="skeleton h-4 w-1/3 mb-3" />
      <div className="skeleton h-8 w-1/2 mb-2" />
      <div className="skeleton h-3 w-2/3" />
    </div>
  );
}

function AnimatedNumber({ value, suffix = "" }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const steps = 40;
    const inc = value / steps;
    let cur = 0;
    const timer = setInterval(() => {
      cur += inc;
      if (cur >= value) { setDisplay(value); clearInterval(timer); }
      else setDisplay(Math.floor(cur));
    }, 1200 / steps);
    return () => clearInterval(timer);
  }, [value]);
  return <>{display.toLocaleString('fr-FR')}{suffix}</>;
}

function getGreeting(t) {
  const h = new Date().getHours();
  if (h < 12) return t('greetingMorning');
  if (h < 18) return t('greetingAfternoon');
  return t('greetingEvening');
}

function RappelsBanner({ rappels, onDismiss, t }) {
  if (!rappels?.has_alerts) return null;
  const total = (rappels.rdv_aujourd_hui?.length || 0) + (rappels.rdv_demain?.length || 0) +
    (rappels.materiaux_a_preparer?.length || 0) + (rappels.chantiers_deadline?.length || 0);
  if (total === 0) return null;

  return (
    <div data-testid="rappels-banner" className="bg-white border border-[#F97316]/30 rounded-2xl p-4 shadow-md shadow-orange-100 animate-fadeIn">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3 flex-1">
          <div className="p-2.5 rounded-xl bg-orange-50 flex-shrink-0 animate-pulse">
            <Bell className="w-5 h-5 text-[#F97316]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-[#1E293B] text-sm">{t('remindersBanner')}</h3>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {rappels.rdv_aujourd_hui?.length > 0 && (
                <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded-full border border-blue-100">
                  {rappels.rdv_aujourd_hui.length} {t('rdvTodayLabel')}
                </span>
              )}
              {rappels.rdv_demain?.length > 0 && (
                <span className="px-2 py-0.5 bg-purple-50 text-purple-600 text-xs rounded-full border border-purple-100">
                  {rappels.rdv_demain.length} {t('rdvTomorrowLabel')}
                </span>
              )}
              {rappels.materiaux_a_preparer?.length > 0 && (
                <span className="px-2 py-0.5 bg-orange-50 text-orange-600 text-xs rounded-full border border-orange-100">
                  {rappels.materiaux_a_preparer.length} {t('materialsToPrepare')}
                </span>
              )}
              {rappels.chantiers_deadline?.length > 0 && (
                <span className="px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded-full border border-red-100">
                  {rappels.chantiers_deadline.length} {t('deadlineSoon')}
                </span>
              )}
            </div>
            {rappels.rdv_aujourd_hui?.length > 0 && (
              <div className="mt-2 space-y-1">
                {rappels.rdv_aujourd_hui.map(rdv => (
                  <p key={rdv.id} className="text-xs text-[#475569]">
                    {rdv.heure} — <strong>{rdv.titre}</strong> ({rdv.lieu})
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
        <button onClick={onDismiss} className="p-1.5 text-[#94A3B8] hover:text-[#1E293B] hover:bg-[#F1F5F9] rounded-lg ml-2 flex-shrink-0 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [chantiers, setChantiers] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [rdvToday, setRdvToday] = useState([]);
  const [demandesDemain, setDemandesDemain] = useState([]);
  const [rappels, setRappels] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [showRappels, setShowRappels] = useState(true);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();
  const { user, isPatron } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const chantiersParams = new URLSearchParams();
      if (user?.id) chantiersParams.append('user_id', user.id);
      if (user?.role) chantiersParams.append('role', user.role);

      const [statsRes, chantiersRes, alertesRes, rdvRes, rappelsRes, demainRes, demandesRes] = await Promise.all([
        axios.get(`${API}/stats/dashboard`),
        axios.get(`${API}/chantiers?${chantiersParams.toString()}`),
        axios.get(`${API}/materiaux/alertes/stock`),
        axios.get(`${API}/rdv/upcoming`).catch(() => ({ data: [] })),
        axios.get(`${API}/rappels`).catch(() => ({ data: null })),
        isPatron() ? axios.get(`${API}/demandes-materiaux/demain`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        axios.get(`${API}/demandes-materiaux?statut=en_attente`).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setChantiers(chantiersRes.data.slice(0, 4));
      setAlertes(alertesRes.data);
      setRdvToday(rdvRes.data.slice(0, 3));
      setRappels(rappelsRes.data);
      setDemandesDemain(demainRes.data);
      setPendingCount(demandesRes.data.length);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally { setLoading(false); }
  };

  const updateDemandeStatus = async (id, statut) => {
    try {
      await axios.put(`${API}/demandes-materiaux/${id}`, { statut });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const getStatusColor = (s) => ({
    en_cours: 'from-blue-500 to-blue-600',
    termine: 'from-emerald-500 to-emerald-600',
    en_pause: 'from-amber-500 to-amber-600'
  }[s] || 'from-slate-400 to-slate-500');

  const getStatusLabel = (s) => ({
    en_cours: t('inProgress'),
    termine: t('completed'),
    en_pause: t('paused')
  }[s] || s);

  if (loading) {
    return (
      <div className="space-y-5 pb-4">
        <div className="h-36 rounded-2xl skeleton" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <SkeletonCard key={i} />)}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard" className="space-y-5 pb-4">
      {/* Rappels Banner */}
      {showRappels && <RappelsBanner rappels={rappels} onDismiss={() => setShowRappels(false)} t={t} />}

      {/* Welcome Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#F97316] via-[#FB923C] to-[#FBBF24] p-5 md:p-7 shadow-lg shadow-orange-200 animate-fadeIn">
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-1">
            <span>{getGreeting(t)}</span>
          </div>
          <h1 className="text-3xl font-bold text-white font-['Barlow_Condensed'] uppercase tracking-wide">
            {user?.prenom || 'Boss'} !
          </h1>
          <div className="flex flex-wrap gap-3 mt-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl px-3 py-1.5">
              <span className="text-white/70 text-xs block">{t('activeSites')}</span>
              <p className="text-white font-bold text-lg">{stats?.chantiers_en_cours || 0}</p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-xl px-3 py-1.5">
              <span className="text-white/70 text-xs block">{t('rdvToday')}</span>
              <p className="text-white font-bold text-lg">{rdvToday.length}</p>
            </div>
            {isPatron() && (
              <div className={`backdrop-blur-sm rounded-xl px-3 py-1.5 ${demandesDemain.length > 0 ? 'bg-red-400/40' : 'bg-white/20'}`}>
                <span className="text-white/70 text-xs block">{t('matsDemain')}</span>
                <p className="text-white font-bold text-lg">{demandesDemain.reduce((acc, d) => acc + (d.materiaux?.length || 0), 0)}</p>
              </div>
            )}
            {isPatron() && pendingCount > 0 && (
              <div className="bg-red-400/40 backdrop-blur-sm rounded-xl px-3 py-1.5">
                <span className="text-white/70 text-xs block">{t('pendingRequests')}</span>
                <p className="text-white font-bold text-lg">{pendingCount}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { path: '/chantiers', icon: Building2, label: t('chantiers'), from: 'from-[#3B82F6]', to: 'to-[#2563EB]', shadow: 'shadow-blue-300/40' },
          { path: '/rdv', icon: Calendar, label: 'RDV', from: 'from-[#8B5CF6]', to: 'to-[#7C3AED]', shadow: 'shadow-purple-300/40' },
          { path: '/demandes', icon: Package, label: t('demandes'), from: 'from-[#F97316]', to: 'to-[#EA580C]', shadow: 'shadow-orange-300/40' },
          isPatron()
            ? { path: '/assistant', icon: Sparkles, label: t('assistant'), from: 'from-[#10B981]', to: 'to-[#059669]', shadow: 'shadow-emerald-300/40' }
            : { path: '/calendrier', icon: Clock, label: t('planning'), from: 'from-[#10B981]', to: 'to-[#059669]', shadow: 'shadow-emerald-300/40' }
        ].map((item, i) => (
          <button
            key={i}
            onClick={() => navigate(item.path)}
            className={`group p-4 rounded-xl bg-gradient-to-br ${item.from} ${item.to} hover:shadow-lg hover:${item.shadow} transition-all duration-300 hover:scale-105 btn-scale`}
          >
            <item.icon className="w-7 h-7 text-white mb-2 group-hover:scale-110 transition-transform" />
            <span className="text-white font-medium text-sm">{item.label}</span>
          </button>
        ))}
      </div>

      {/* Today Section - Stats */}
      <div>
        <h2 className="text-sm font-semibold text-[#94A3B8] uppercase tracking-wider mb-3">{t('todaySection')}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="bg-white border-[#E2E8F0] hover:shadow-md transition-all cursor-pointer group interactive-card" onClick={() => navigate('/chantiers')}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#94A3B8] font-mono mb-1">{t('actifs')}</p>
                  <p className="text-3xl font-bold text-[#1E293B] group-hover:text-[#3B82F6] transition-colors">
                    <AnimatedNumber value={stats?.chantiers_en_cours || 0} />
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <Building2 className="w-5 h-5 text-[#3B82F6]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E2E8F0] hover:shadow-md transition-all cursor-pointer group interactive-card" onClick={() => navigate('/materiaux')}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#94A3B8] font-mono mb-1">{t('materiaux')}</p>
                  <p className="text-3xl font-bold text-[#1E293B] group-hover:text-[#F97316] transition-colors">
                    <AnimatedNumber value={stats?.total_materiaux || 0} />
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center group-hover:bg-orange-100 transition-colors">
                  <Package className="w-5 h-5 text-[#F97316]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`bg-white border-[#E2E8F0] transition-all interactive-card ${stats?.taches_urgentes > 0 ? 'border-red-200 bg-red-50/30' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#94A3B8] font-mono mb-1">{t('urgences')}</p>
                  <p className={`text-3xl font-bold ${stats?.taches_urgentes > 0 ? 'text-red-600' : 'text-[#1E293B]'}`}>
                    <AnimatedNumber value={stats?.taches_urgentes || 0} />
                  </p>
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stats?.taches_urgentes > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                  <Clock className={`w-5 h-5 ${stats?.taches_urgentes > 0 ? 'text-red-500' : 'text-slate-400'}`} />
                </div>
              </div>
            </CardContent>
          </Card>

          {isPatron() ? (
            <Card className="bg-white border-[#E2E8F0] hover:shadow-md transition-all group interactive-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#94A3B8] font-mono mb-1">{t('budgetStat')}</p>
                    <p className="text-2xl font-bold text-[#1E293B] group-hover:text-emerald-600 transition-colors">
                      <AnimatedNumber value={stats?.budget_total || 0} suffix="€" />
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <Euro className="w-5 h-5 text-emerald-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white border-[#E2E8F0] hover:shadow-md transition-all cursor-pointer group interactive-card" onClick={() => navigate('/demandes')}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#94A3B8] font-mono mb-1">{t('pendingRequests')}</p>
                    <p className="text-3xl font-bold text-[#1E293B] group-hover:text-[#F97316] transition-colors">
                      <AnimatedNumber value={pendingCount} />
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-[#F97316]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Chantiers récents */}
        <Card className="lg:col-span-2 bg-white border-[#E2E8F0] shadow-sm overflow-hidden">
          <CardHeader className="border-b border-[#F1F5F9] bg-gradient-to-r from-blue-50 to-transparent py-3">
            <CardTitle className="text-base font-semibold text-[#1E293B] flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#3B82F6]" />
              {t('recentSites')}
              <Button variant="ghost" size="sm" className="ml-auto text-[#3B82F6] hover:bg-blue-50 text-xs" onClick={() => navigate('/chantiers')}>
                {t('seeAll')} <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {chantiers.length === 0 ? (
              <div className="p-8 text-center">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-[#CBD5E1]" />
                <p className="text-[#94A3B8]">{t('noChantier')}</p>
                {isPatron() && (
                  <Button onClick={() => navigate('/chantiers')} className="mt-3 bg-[#3B82F6] text-white rounded-lg text-sm">
                    {t('createFirst')}
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-[#F1F5F9]">
                {chantiers.map(c => (
                  <div
                    key={c.id}
                    className="p-3 hover:bg-[#F8FAFC] transition-colors cursor-pointer group"
                    onClick={() => navigate('/chantiers')}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${getStatusColor(c.statut)} flex items-center justify-center flex-shrink-0`}>
                        <Building2 className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-[#1E293B] text-sm group-hover:text-[#3B82F6] transition-colors truncate">{c.nom}</h4>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            {c.photos_count > 0 && (
                              <span className="text-xs text-[#94A3B8] flex items-center gap-0.5">
                                <span className="text-[10px]">📸</span>{c.photos_count}
                              </span>
                            )}
                            {c.notes?.length > 0 && (
                              <span className="text-xs text-[#94A3B8] flex items-center gap-0.5">
                                <span className="text-[10px]">📝</span>{c.notes.length}
                              </span>
                            )}
                            <span className="text-xs font-mono text-[#94A3B8]">{c.progression}%</span>
                          </div>
                        </div>
                        <div className="h-1.5 bg-[#EEF2F7] rounded-full mt-1.5 overflow-hidden">
                          <div className={`h-full rounded-full bg-gradient-to-r ${getStatusColor(c.statut)} transition-all`} style={{ width: `${c.progression}%` }} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right column */}
        <div className="space-y-5">
          {/* Matériaux pour demain - Patrons only */}
          {isPatron() && (
            <Card className={`bg-white border-[#E2E8F0] shadow-sm overflow-hidden ${demandesDemain.length > 0 ? 'border-l-4 border-l-[#F97316]' : ''}`}>
              <CardHeader className="border-b border-[#F1F5F9] bg-gradient-to-r from-orange-50 to-transparent py-3">
                <CardTitle className="text-base font-semibold text-[#1E293B] flex items-center gap-2">
                  <Truck className="w-4 h-4 text-[#F97316]" />
                  {t('matsDemain')}
                  {demandesDemain.length > 0 && (
                    <span className="ml-auto bg-[#F97316] text-white text-xs px-2 py-0.5 rounded-full">
                      {demandesDemain.reduce((a, d) => a + (d.materiaux?.length || 0), 0)}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                {demandesDemain.length === 0 ? (
                  <div className="text-center py-3">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-1 text-emerald-400" />
                    <p className="text-emerald-600 text-sm font-medium">{t('allReady')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {demandesDemain.map((group, idx) => (
                      <div key={idx}>
                        <p className="text-xs font-semibold text-[#475569] mb-1.5">{group.chantier?.nom}</p>
                        <div className="space-y-1.5">
                          {group.materiaux?.map(mat => (
                            <div key={mat.id} className="flex items-center justify-between gap-2">
                              <span className="text-xs text-[#1E293B] flex-1 truncate">
                                {mat.materiau_nom} <span className="text-[#94A3B8]">×{mat.quantite} {mat.unite}</span>
                              </span>
                              {mat.statut === 'en_attente' && (
                                <div className="flex gap-1">
                                  <button onClick={() => updateDemandeStatus(mat.id, 'approuve')} className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded font-medium hover:bg-emerald-200 transition-colors">✓</button>
                                  <button onClick={() => updateDemandeStatus(mat.id, 'refuse')} className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded font-medium hover:bg-red-200 transition-colors">✗</button>
                                </div>
                              )}
                              {mat.statut === 'approuve' && <span className="text-xs text-emerald-600 font-medium">✓ OK</span>}
                              {mat.statut === 'refuse' && <span className="text-xs text-red-500 font-medium">✗</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    <Button size="sm" variant="outline" className="w-full border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9] rounded-lg text-xs" onClick={() => navigate('/demandes')}>
                      {t('seeAll')} <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Alertes Stock */}
          <Card className={`bg-white border-[#E2E8F0] shadow-sm overflow-hidden ${alertes.length > 0 ? 'border-l-4 border-l-red-400' : 'border-l-4 border-l-emerald-400'}`}>
            <CardHeader className={`border-b border-[#F1F5F9] py-3 ${alertes.length > 0 ? 'bg-gradient-to-r from-red-50 to-transparent' : 'bg-gradient-to-r from-emerald-50 to-transparent'}`}>
              <CardTitle className="text-base font-semibold text-[#1E293B] flex items-center gap-2">
                {alertes.length > 0 ? <AlertTriangle className="w-4 h-4 text-red-500" /> : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                {t('stockAlerts')} {alertes.length > 0 ? `(${alertes.length})` : ''}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              {alertes.length === 0 ? (
                <div className="text-center py-3">
                  <p className="text-emerald-600 text-sm font-medium">{t('stockOK')}</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {alertes.slice(0, 4).map(a => (
                    <div key={a.id} className="flex items-center gap-2 p-2 bg-red-50 rounded-lg">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                      <span className="text-xs text-[#1E293B] flex-1 truncate">{a.nom}</span>
                      <span className="text-xs text-red-500 font-mono">{a.quantite} {a.unite}</span>
                    </div>
                  ))}
                  {alertes.length > 4 && <p className="text-xs text-[#94A3B8] text-center">+{alertes.length - 4} autres</p>}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Prochains RDV */}
          <Card className="bg-white border-[#E2E8F0] shadow-sm overflow-hidden">
            <CardHeader className="border-b border-[#F1F5F9] bg-gradient-to-r from-purple-50 to-transparent py-3">
              <CardTitle className="text-base font-semibold text-[#1E293B] flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#8B5CF6]" />
                {t('nextRDV')}
                <Button variant="ghost" size="sm" className="ml-auto text-[#8B5CF6] hover:bg-purple-50 text-xs" onClick={() => navigate('/rdv')}>
                  <ArrowRight className="w-3 h-3" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              {rdvToday.length === 0 ? (
                <div className="text-center py-3">
                  <p className="text-[#94A3B8] text-sm">{t('noRDV')}</p>
                  <Button variant="outline" size="sm" className="mt-2 border-purple-200 text-[#8B5CF6] hover:bg-purple-50 text-xs rounded-lg" onClick={() => navigate('/rdv')}>
                    {t('planBtn')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {rdvToday.map(rdv => (
                    <div key={rdv.id} className="flex items-center gap-2.5 p-2 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100/60 transition-colors" onClick={() => navigate('/rdv')}>
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#8B5CF6] to-[#7C3AED] flex flex-col items-center justify-center text-white flex-shrink-0">
                        <span className="text-[9px] leading-none opacity-80">{new Date(rdv.date).toLocaleDateString('fr-FR', { weekday: 'short' })}</span>
                        <span className="text-sm font-bold leading-none">{new Date(rdv.date).getDate()}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[#1E293B] text-xs font-medium truncate">{rdv.titre}</p>
                        <p className="text-[#94A3B8] text-xs">{rdv.heure} · {rdv.lieu}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
